import { IrbEditor } from "@irb/irb-editors/irb-editors.base";
import { IrbAppEvents, IEvent, IrbEditorsEvents } from '@irb/irb-common/events';
import { Observable, of, interval, Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import {ServiceLocator} from "@irb-common/services/locator.service";
import { ISaveRebalanceResponse, ISaveRebalanceStatus } from '@irb/irb-editors/rebalance/irb-rebalance.model';
import { IrbCreateRebalanceService } from '@irb/irb-create-rebalance/irb-create-rebalance.service';
import {IAlertDialogData} from "@irb/app.model";
import { Router } from '@angular/router';

export class IrbRebalanceEditor extends IrbEditor {
  saveLabel: string = "Process";
  analysisName: string;
  analysisType: string;
  protected datePipe: DatePipe;
  checkSaveRebalanceSub: Subscription;
  protected crbservice: IrbCreateRebalanceService;
  protected portfolioThreshold: number = 2000;
  protected securityThreshold: number = 50;
  protected router: Router;

  constructor() {
    super();
    this.datePipe = ServiceLocator.injector.get(DatePipe);
    this.crbservice = ServiceLocator.injector.get(IrbCreateRebalanceService);
    this.router = ServiceLocator.injector.get(Router);

    this.as.getUserDetails().subscribe((res: any) => {
      if (res && res.createRebalanceThreshold) {
        this.portfolioThreshold = res.createRebalanceThreshold;
      }

      if (res && res.reblResSummarySecurityThreshold) {
        this.securityThreshold = res.reblResSummarySecurityThreshold;
      }
    });
  }

  onInputChange() {
    const ignoreList: Array<string> = [];

    if (this.data && this.data.scheduler) {
      if (this.data.scheduler.scheduleType && this.data.scheduler.scheduleType === "Recurring"
        && !(this.data.analysisType === "Manual adjustment") && !(this.data.analysisType === "Covered call")) {
        this.saveLabel = "Schedule";
        this.ignoreUnsavedChanges = false;
      } else {
        this.saveLabel = "Process";
        ignoreList.push("scheduler");
        this.ignoreUnsavedChanges = true;
      }
    }

    if(!this.data.isUIAllPort && this.data.smartFilterDto) {
      const noSmartFilter = !(this.data.smartFilterDto.sfCategoryDtoList?.length > 0) && !(this.data.smartFilterDto.sfModelDtoList?.length > 0) && !this.data.smartFilterDto.smartFilterDays && !this.data.smartFilterDto.modelUpdated;
      const noPortfolios = this.data.smartFilterDto.analysisPortDtoList.length === 0;

      //If scheduled and not rebalance all, must have either a smart filter or portfolios
      //If not scheduled and not rebalance all, must have portfolios
      this.isValid = !((this.saveLabel === 'Schedule' && noSmartFilter && noPortfolios) || (this.saveLabel !== 'Schedule' && noPortfolios));
    } else {
      this.isValid = true;
    }

    if (this.data && this.data.isNew) {
      ignoreList.push("isSysSuggestion");
      ignoreList.push("analysisName");
      ignoreList.push("tradeExpln");
    }

    if (this.data.analysisType === "Adjust securities") {
      ignoreList.push("harvestTaxLossDto");
      ignoreList.push("replaceSecsDto");
      ignoreList.push("coveredCallDTO");
      ignoreList.push("isDeleted");
      ignoreList.push("versionId");

      if (this.data.isNew) {
        ignoreList.push("smartFilterDto");
        
        if (this.data.isUIAllPort !== true) {
          ignoreList.push("isUIAllPort");
        }
      }

      const adjustMultipleSecurities = this.data && this.data.adjustSecDto && this.data.adjustSecDto.adjustMultipleSecurities ? this.data.adjustSecDto.adjustMultipleSecurities : [];
      
      if (adjustMultipleSecurities.length === 0 ||
        (this.data.smartFilterDto.analysisPortDtoList?.length === 0 && !this.data.isUIAllPort)) {
        ignoreList.push("adjustSecDto");
        ignoreList.push("smartFilterDto");
        this.isValid = false;
      } else {
        this.isValid = true;
      }
    }

    if (this.data.analysisType === "Harvest tax losses" || this.data.analysisType === "Tax Loss Harvest") {
      ignoreList.push("adjustSecDto");
      ignoreList.push("replaceSecsDto");
      ignoreList.push("coveredCallDTO");
      ignoreList.push("isDeleted");
      ignoreList.push("versionId");

      if (!this.data.harvestTaxLossDto.isApplyGlobalTLH) {
        if (this.data.isNew) {
          ignoreList.push("smartFilterDto");

          if (this.data.isUIAllPort !== true) {
            ignoreList.push("isUIAllPort");
          }
        }

        const htlSecsdtoList = this.data && this.data.harvestTaxLossDto && this.data.harvestTaxLossDto.htlSecsdtoList ?
          this.data.harvestTaxLossDto.htlSecsdtoList.filter((x: any) => !x.isUIdeleted) : [];

        if (htlSecsdtoList.length === 0 ||
          (this.data.smartFilterDto.analysisPortDtoList?.length === 0 && !this.data.isUIAllPort)) {
          ignoreList.push("harvestTaxLossDto");
          ignoreList.push("smartFilterDto");
          this.isValid = false;
        } else
          this.isValid = true;
      }
    }

    if (this.data.analysisType === "Covered call") {
      ignoreList.push("adjustSecDto");
      ignoreList.push("replaceSecsDto");
      ignoreList.push("harvestTaxLossDto");
      ignoreList.push("isDeleted");
      ignoreList.push("versionId");

      if (this.data.isNew) {
        ignoreList.push("smartFilterDto");
        if (this.data.isUIAllPort !== true)
          ignoreList.push("isUIAllPort");
      }

      const allocations = this.data && this.data.coveredCallDTO && this.data.coveredCallDTO.allocations ? this.data.coveredCallDTO.allocations : [];

      if (allocations.length === 0 ||
        this.data.smartFilterDto.analysisPortDtoList?.length === 0) {
        ignoreList.push("coveredCallDTO");
        ignoreList.push("smartFilterDto");
      }
    }

    if (this.data.analysisType === "Replace securities") {
      ignoreList.push("adjustSecDto");
      ignoreList.push("coveredCallDTO");
      ignoreList.push("harvestTaxLossDto");
      ignoreList.push("isDeleted");
      ignoreList.push("versionId");

      if (this.data.isNew) {
        if (this.data.isUIAllPort !== true)
          ignoreList.push("isUIAllPort");
      }

      const replaceSecsDto = this.data && this.data.replaceSecsDto && this.data.replaceSecsDto.replaceMultipleSecurities ? this.data.replaceSecsDto.replaceMultipleSecurities : [];

      if (replaceSecsDto.length === 0 || (this.data.smartFilterDto.analysisPortDtoList?.length === 0 && !this.data.isUIAllPort)) {
        ignoreList.push("replaceSecsDto");
        ignoreList.push("smartFilterDto");
        this.isValid = false;
      } else {
        this.isValid = true;
      }
    }

    if (this.data.analysisType === "Fully rebalance securities & cash" || this.data.analysisType === "Full rebalance" || this.data.analysisType === "Generate cash only" ||
      this.data.analysisType === "Generate and/or invest cash" || this.data.analysisType === "Invest cash only" ||
      this.data.analysisType === "Manual adjustment" || this.data.analysisType === "Tax Loss Harvesting & Full rebalance") {
      ignoreList.push("adjustSecDto");
      ignoreList.push("harvestTaxLossDto");
      ignoreList.push("replaceSecsDto");
      ignoreList.push("coveredCallDTO")
      ignoreList.push("isDeleted");
      ignoreList.push("versionId");
    }

    ignoreList.push("portfolioNameGroup");
    ignoreList.push("extraCashAmount");
    ignoreList.push("sfCategoryDtoList");
    ignoreList.push("sfModelDtoList");
    ignoreList.push("smartFilterDays");


    if (this.data?.analysisId && this.data?.scheduler) {
      if (this.data.scheduler.endDateType === 'End by (date)' && this.data.scheduler.endByDate) {
        const endEasternDate: any = new Date(this.util.getEasternTime(this.data.scheduler.endByDate));
        const currentEasternDate: any = new Date(this.util.getEasternTime(new Date().toString()));
        if (endEasternDate.getTime() < currentEasternDate.getTime()) {
          this.isValid = false;
        }
      }
    }

    if (this.data.scheduler?.scheduleType === "Recurring" && this.isValid && !this.util.compare(this.originalData.scheduler, this.data.scheduler)) {	
      this.data.scheduler.isActive = true;
    }

    super.onInputChange(ignoreList);
  }

  applyRebalanceData(event: any, data: any, todayDate: any) {
    if (event.data && event.data.rebalanceParams) {
      this.data = data;
      this.originalData = this.util.clone(this.data);
      this.isModified = false;

      data = Object.assign(data, event.data.rebalanceParams);
      data.analysisType = this.analysisType;
      if (data.isSysSuggestion) { data.analysisName = this.analysisName + todayDate; }
      if (data.smartFilterDto) { data.smartFilterDto.analysisType = this.analysisType; }
      this.data = data;
      this.onInputChange();
      this.isLoading = false;
    } else {
      this.load(data);
    }
  }

  smartFilterChange(event: any) {
    if (event) {
      this.onInputChange();
    }
  }

  setGlobalError() {
    let m = ' saveRebalance failed.';
    m += ' analysisType=' + this.data.analysisType;
    m += ' analysisName=' + this.data.analysisName;
    throw new Error(m);
  }

  close(event?: IEvent) {
    super.close();
    let d = (typeof event !== "undefined") ? event.data : {"slot": this.slot};

    if(!d.source || (d.source && d.source !== "navigation")) {
      this.as.fireEvent(IrbAppEvents.FIRE_TRACKING_EVENT);
    }
  }

  trackEditEvent(eventData: any) {
    this.ats.trackEditRebalance(eventData.schedulerFrequency);
  }

  validateInput(): Observable<string[]> {
    return of(this.validateDataInput());
  }

  validateDataInput(): string[] {
    const errors: string[] = [];
    // if (this.data?.smartFilterDto?.smartFilterDays !== undefined && this.data?.smartFilterDto?.smartFilterDays !== null && this.data?.smartFilterDto?.smartFilterDays !== '') {
    //   if (!(this.data?.smartFilterDto?.smartFilterDays > 0 && this.data?.smartFilterDto?.smartFilterDays < 1000)) {
    //     errors.push('Please enter number of "Not rebalanced in days" between 1 and 999.')
    //   }
    // }
    if(this.data.schedulerFrequency === "Daily" || this.data.schedulerFrequency === "Weekly" || this.data.schedulerFrequency === "Monthly"){
      if(this.data.scheduler.reccuringFreqType === "Daily"){
        this.data.scheduler.everyNDays = parseInt(this.data.scheduler.everyNDays);

        if(isNaN(this.data.scheduler.everyNDays) || this.data.scheduler.everyNDays < 1 || this.data.scheduler.everyNDays > 365) {
          errors.push('Please enter a number of days between 1 and 365.')
        }
      } else if(this.data.scheduler.reccuringFreqType === "Weekly"){
        this.data.scheduler.everyNWeeks = parseInt(this.data.scheduler.everyNWeeks);

        if(isNaN(this.data.scheduler.everyNWeeks) || this.data.scheduler.everyNWeeks < 1 || this.data.scheduler.everyNWeeks > 52) {
          errors.push('Please enter a number of weeks between 1 and 52.')
        }
        if(!this.data.scheduler.tuesday && !this.data.scheduler.wednesday && !this.data.scheduler.thursday && !this.data.scheduler.friday && !this.data.scheduler.saturday) {
          errors.push("Please select day(s) of the week for the rebalance.");
        }
      } else if(this.data.scheduler.reccuringFreqType === "Monthly"){
        this.data.scheduler.everyNMonths = parseInt(this.data.scheduler.everyNMonths);

        if(isNaN(this.data.scheduler.everyNMonths) || this.data.scheduler.everyNMonths < 1 || this.data.scheduler.everyNMonths > 12) {
          errors.push('Please enter a number of months between 1 and 12.')
        }
        if(this.data.scheduler.monthlyRecurrenceType === "a specific date"){
          this.data.scheduler.dateNumber = parseInt(this.data.scheduler.dateNumber);

          if(isNaN(this.data.scheduler.dateNumber) || this.data.scheduler.dateNumber < 1 || this.data.scheduler.dateNumber > 31) {
            errors.push('Please enter a number of days between 1 and 31.')
          }
        }
      }

      if(this.data.scheduler.startDate === null || this.data.scheduler.startDate === undefined) {
        errors.push('Please choose a Start Date.')
      }

      if(this.data.scheduler.endDateType != null || this.data.scheduler.endDateType !== undefined){
        if(this.data.scheduler.endDateType === 'End by (date)') {
          let date1 = this.datePipe.transform(this.data.scheduler.startDate, "yyyy-MM-dd");
          let date2 = this.datePipe.transform(this.data.scheduler.endByDate, "yyyy-MM-dd");

          if(date1 > date2){
            errors.push('The end date for this scheduled rebalance cannot occur before the start date.')
          }
        } else if (this.data.scheduler.endDateType === 'End after # occurrences') {
          if(this.data.scheduler.endAfterNTimes === null || this.data.scheduler.endAfterNTimes === undefined
              || this.data.scheduler.endAfterNTimes === "" || this.data.scheduler.endAfterNTimes < 1 || this.data.scheduler.endAfterNTimes > 1000 ){
            errors.push('Please enter end after # occurrences between 1 and 1000.')
          }
        } else {
          // no end date
        }
      }
    }

    if(!this.data.isUIAllPort && this.data.smartFilterDto) {
      const noSmartFilter = !this.data.smartFilterDto.sfCategoryDtoList && !this.data.smartFilterDto.sfModelDtoList && !this.data.smartFilterDto.smartFilterDays && !this.data.smartFilterDto.modelUpdated;
      const noPortfolios = this.data.smartFilterDto.analysisPortDtoList.length === 0;

      if ((this.saveLabel === 'Schedule' && noSmartFilter && noPortfolios) || (this.saveLabel !== 'Schedule' && noPortfolios)) {
        errors.push('This rebalance cannot be submitted because it does not contain any portfolios. To include portfolios, add them individually or via auto filter in the included portfolios section.');
      }
    }
  
    return errors;
  }

  checkSaveRebalanceAsyncStatus(rprasId: string) {
    this.checkSaveRebalanceSub = interval(3000).subscribe(() => {
      this.crbservice.getSaveRebalanceStatus(rprasId).subscribe((response: ISaveRebalanceStatus) => {
        if(response.status.toUpperCase() === "COMPLETED") {
          this.checkSaveRebalanceSub.unsubscribe();
          this.completeSaveRebalance({
            responseStatus: "SUCCESS",
            responseUtilityMap: {
              analysisId: response.analysisId,
              rebalanceResultId: response.rebalanceResultId,
              rebalanceName: response.rebalanceName 
            }
          } as ISaveRebalanceResponse);
        } else if(response.status.toUpperCase() === "FAILED") {
          this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
            dType: 'ERROR',
            dTitle: '',
            dProceedBtnLabel: '',
            msg: response.failMsg,
            onProceed: () => {}
          } as IAlertDialogData);
          this.checkSaveRebalanceSub.unsubscribe();
          this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);
        } else if(response.status.toUpperCase() === "INPROGRESS") {
        }
      });
    });
  }

  completeSaveRebalance(_response: ISaveRebalanceResponse) {}
  
  navigateToResultSummary(rebalanceResultId: any) {
    this.router.navigateByUrl("/rebalancing/rebalance-results", { skipLocationChange: true }).
    then(() => this.router.navigate(["/rebalancing/result-summary/", rebalanceResultId]));
  }

  navigateToScheduledRebalances() {
    this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, { "slot": this.slot });
    this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot}); 
    this.router.navigate(['/rebalancing/scheduled-rebalances']);    
  }
}
